import { useQuery } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import type { ILead } from 'src/services/api/leads/types';
import { type EMessageChannel, type ELeadStage } from 'src/services/api';

const PATH = 'api/landlord/conversations/search';

/**
 * NOTE: any filter may be undefined - meaning no filtering on that field
 */
export type TConversationsFilter = {
    /**
     * String representation of user IDs.
     */
    assigneeIds?: string[];
    ingestionSources?: string[];
    leadStages?: ELeadStage[];
    /**
     * String representation of label IDs.
     */
    leadLabelIds?: string[];
    /**
     * If set, filter by lead archival status
     */
    leadIsArchived?: boolean;
    /**
     * String representation of label IDs.
     */
    excludeLeadLabelIds?: string[];
    /**
     * String representation of property IDs.
     */
    propertyIds?: string[];
    // When true, only conversations with no property will be shown.
    // We can overload the propertyId field to also represent the absence of a property.
    // But, this is more explicit and less error-prone.
    hasNoProperty?: boolean;
    /**
     * String representation of label IDs.
     */
    propertyLabelIds?: string[];
};

/**
 * NOTE: This does not use a nested structure to make it easier to make GET requests
 * This is the format of the GET request payload
 */
// type ISearchOptions = {
//     q?: string;

//     /**
//      * IDs
//      */
//     lead_labels?: number[];
//     /**
//      * IDs
//      */
//     property_labels?: number[];
//     /**
//      * IDs
//      */
//     properties?: number[];
//     /**
//      * IDs
//      */
//     lead_owners?: number[];
//     lead_stages?: string[];
//     lead_ingestion_sources?: string[];
// };

export interface IConversationMatch {
    lead_uuid: string;
    lead_name: string;
    lead_email: string | null;
    lead_phone_number: string | null;
    matching_message_uuid: string;
    matching_message_contents: string;
    matching_message_inserted_at: string;
}

interface ISearchResult {
    match: IConversationMatch;
    lead: ILead;
    msg_channel: EMessageChannel;
}

export type ISearchResponse = {
    results: ISearchResult[];
    has_more: boolean;
}


export const isSearchEnabled = (q?: string, filters?: TConversationsFilter) => {
    const hasQuery = (q !== undefined) && ((q || '').length > 2);
    const hasNonEmptyFilters = (filters !== undefined && Object.keys(filters).length > 0);
    return hasQuery || hasNonEmptyFilters;
};

export const useConversationsSearch = (q?: string, filters?: TConversationsFilter) => {
    return useQuery<ISearchResponse>(
        [queryKeys.conversations + '/search', q, filters],
        async () => {
            const searchParams = new URLSearchParams();
            if (q) {
                searchParams.set('q', q);
            }

            // no translation from string to int necessary since this is a GET request
            const SUPPORTED_FILTER_ARRAYS = {
                'assigneeIds': 'lead_owners',
                'ingestionSources': 'lead_ingestion_sources',
                'leadStages': 'lead_stages',
                'leadLabelIds': 'lead_labels',
                'propertyLabelIds': 'property_labels',
                'propertyIds': 'properties',
            };

            // fill in filters in getParams
            for (const [key, value] of Object.entries(filters || {})) {
                if (value === undefined) {
                    continue;
                } else if (Array.isArray(value) && value.length === 0) {
                    continue;
                }

                if (key in SUPPORTED_FILTER_ARRAYS) {
                    const paramName = SUPPORTED_FILTER_ARRAYS[key as keyof typeof SUPPORTED_FILTER_ARRAYS];
                    if (!Array.isArray(value)) {
                        console.error(`Expected array for ${key} filter, got:`, value);
                        continue;
                    }
                    searchParams.set(paramName, value.join(','));
                    continue;
                } else if (key === 'hasNoProperty' && value === false) {
                    // default value, do nothing
                } else if (typeof value === 'boolean' && key === 'leadIsArchived') {
                    searchParams.set('lead_is_archived', value ? 'true' : 'false');
                } else {
                    console.warn('Key not supported:', key);
                }
            }

            const res = await apiClient(PATH, { searchParams }).json<ISearchResponse>();
            return res;
        },
        {
            enabled: isSearchEnabled(q, filters),
            // this makes filtering smoother
            keepPreviousData: true,
            retry: 1,
        }
    );
};
